import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useAuthStore } from "@/lib/auth";
import { usePortalDetail } from "@/routes/portals.$id";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

export const Route = createFileRoute("/portals/$id/analytics")({
	component: PortalAnalytics,
});

interface FunnelData {
	searches: number;
	product_views: number;
	add_to_carts: number;
	checkouts: number;
	purchases: number;
	revenue_cents: number;
	unique_visitors: number;
}

interface TrendingProduct {
	product_id: string;
	title: string;
	impressions: number;
	clicks: number;
	purchases: number;
}

interface TopQuery {
	query: string;
	count: number;
	conversion_rate: number;
}

interface EventDailyRow {
	date: string;
	event_type: string;
	count: number;
	unique_visitors: number;
	revenue_cents: number;
}

interface AssociationPair {
	product_a_id: string;
	product_a_title: string;
	product_b_id: string;
	product_b_title: string;
	lift: number;
	co_purchase_count: number;
}

function PortalAnalytics() {
	const { portalApiUrl, keys } = usePortalDetail();
	const { t } = useTranslation("portals");
	const token = useAuthStore((s) => s.token);
	const [days, setDays] = useState("30");
	const activeKey = keys.find((k) => !k.revokedAt);

	const apiHeaders: HeadersInit = activeKey?.tokenPrefix
		? { "X-API-Key": activeKey.tokenPrefix }
		: token
			? { Authorization: `Bearer ${token}` }
			: {};

	const { data: funnel } = useQuery<FunnelData>({
		queryKey: ["portal-funnel", portalApiUrl, days],
		queryFn: async () => {
			const res = await fetch(
				`${portalApiUrl}/api/v1/portal/analytics/funnel?days=${days}`,
				{ headers: apiHeaders },
			);
			if (!res.ok) return null;
			return res.json();
		},
		enabled: !!portalApiUrl,
	});

	const { data: eventDaily } = useQuery<EventDailyRow[]>({
		queryKey: ["portal-event-daily", portalApiUrl, days],
		queryFn: async () => {
			const res = await fetch(
				`${portalApiUrl}/api/v1/portal/analytics/event-daily?days=${days}`,
				{ headers: apiHeaders },
			);
			if (!res.ok) return [];
			const data = await res.json();
			return data.data ?? [];
		},
		enabled: !!portalApiUrl,
	});

	const { data: trending } = useQuery<TrendingProduct[]>({
		queryKey: ["portal-trending", portalApiUrl, days],
		queryFn: async () => {
			const res = await fetch(
				`${portalApiUrl}/api/v1/portal/analytics/trending?days=${days}`,
				{ headers: apiHeaders },
			);
			if (!res.ok) return [];
			const data = await res.json();
			return data.products ?? [];
		},
		enabled: !!portalApiUrl,
	});

	const { data: topQueries } = useQuery<TopQuery[]>({
		queryKey: ["portal-top-queries", portalApiUrl, days],
		queryFn: async () => {
			const res = await fetch(
				`${portalApiUrl}/api/v1/portal/analytics/top-queries?days=${days}`,
				{ headers: apiHeaders },
			);
			if (!res.ok) return [];
			const data = await res.json();
			return data.queries ?? [];
		},
		enabled: !!portalApiUrl,
	});

	const { data: associations } = useQuery<AssociationPair[]>({
		queryKey: ["portal-associations", portalApiUrl],
		queryFn: async () => {
			const res = await fetch(
				`${portalApiUrl}/api/v1/portal/analytics/associations?limit=10`,
				{ headers: apiHeaders },
			);
			if (!res.ok) return [];
			const data = await res.json();
			return data.pairs ?? [];
		},
		enabled: !!portalApiUrl,
	});

	// Pivot event_daily into per-date rows for Recharts
	const chartData = useMemo(() => {
		if (!eventDaily?.length) return [];
		const byDate = new Map<
			string,
			{ date: string; views: number; purchases: number; revenue: number; visitors: number }
		>();
		for (const row of eventDaily) {
			const existing = byDate.get(row.date) ?? {
				date: row.date,
				views: 0,
				purchases: 0,
				revenue: 0,
				visitors: 0,
			};
			if (row.event_type === "product_view")
				existing.views += row.count;
			if (row.event_type === "purchase") {
				existing.purchases += row.count;
				existing.revenue += row.revenue_cents / 100;
			}
			existing.visitors = Math.max(existing.visitors, row.unique_visitors);
			byDate.set(row.date, existing);
		}
		return Array.from(byDate.values()).sort((a, b) =>
			a.date.localeCompare(b.date),
		);
	}, [eventDaily]);

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-semibold">{t("analytics")}</h2>
				<Select value={days} onValueChange={setDays}>
					<SelectTrigger className="w-[140px] h-8 text-sm">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="7">{t("days_7")}</SelectItem>
						<SelectItem value="30">{t("days_30")}</SelectItem>
						<SelectItem value="90">{t("days_90")}</SelectItem>
						<SelectItem value="365">{t("days_365")}</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{/* Metric Cards */}
			{funnel ? (
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
					<StatCard label={t("stat_searches")} value={funnel.searches} />
					<StatCard label={t("stat_views")} value={funnel.product_views} />
					<StatCard
						label={t("stat_add_to_cart")}
						value={funnel.add_to_carts}
					/>
					<StatCard label={t("stat_purchases")} value={funnel.purchases} />
					<StatCard label={t("stat_checkouts")} value={funnel.checkouts} />
					<StatCard
						label={t("stat_revenue")}
						value={`${(funnel.revenue_cents / 100).toLocaleString()} ₽`}
					/>
					<StatCard
						label={t("stat_visitors")}
						value={funnel.unique_visitors}
					/>
				</div>
			) : (
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<div
							key={i}
							className="rounded-lg border p-4 animate-pulse bg-muted/30 h-20"
						/>
					))}
				</div>
			)}

			{/* Revenue + Visitors Time Series */}
			{chartData.length > 1 && (
				<div>
					<h3 className="text-sm font-medium mb-3">
						{t("chart_revenue_visitors")}
					</h3>
					<div className="rounded-lg border p-4">
						<ResponsiveContainer width="100%" height={240}>
							<AreaChart data={chartData}>
								<CartesianGrid
									strokeDasharray="3 3"
									className="stroke-muted"
								/>
								<XAxis
									dataKey="date"
									tick={{ fontSize: 11 }}
									tickFormatter={(d) => d.slice(5)}
								/>
								<YAxis
									yAxisId="left"
									tick={{ fontSize: 11 }}
									tickFormatter={(v) =>
										v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
									}
								/>
								<YAxis
									yAxisId="right"
									orientation="right"
									tick={{ fontSize: 11 }}
								/>
								<Tooltip
									contentStyle={{
										fontSize: 12,
										borderRadius: 8,
										border: "1px solid var(--border)",
									}}
									formatter={((value: unknown, name: unknown) => [
										name === "revenue"
											? `${Number(value).toLocaleString()} ₽`
											: Number(value).toLocaleString(),
										name === "revenue"
											? t("stat_revenue")
											: t("stat_visitors"),
									]) as never}
								/>
								<Area
									yAxisId="left"
									dataKey="revenue"
									stroke="hsl(var(--chart-1))"
									fill="hsl(var(--chart-1))"
									fillOpacity={0.15}
									strokeWidth={2}
									name="revenue"
								/>
								<Area
									yAxisId="right"
									dataKey="visitors"
									stroke="hsl(var(--chart-2))"
									fill="hsl(var(--chart-2))"
									fillOpacity={0.1}
									strokeWidth={1.5}
									name="visitors"
								/>
							</AreaChart>
						</ResponsiveContainer>
					</div>
				</div>
			)}

			{/* Conversion Funnel */}
			{funnel && (
				<div>
					<h3 className="text-sm font-medium mb-3">{t("funnel_title")}</h3>
					<FunnelChart funnel={funnel} t={t} />
				</div>
			)}

			{/* Product Associations */}
			{associations && associations.length > 0 && (
				<div>
					<h3 className="text-sm font-medium mb-3">
						{t("associations_title")}
					</h3>
					<div className="rounded-lg border overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow className="border-b bg-muted/30">
									<TableHead className="text-left px-4 py-2 font-medium">
										{t("association_product_a")}
									</TableHead>
									<TableHead className="text-center px-2 py-2 font-medium">
										+
									</TableHead>
									<TableHead className="text-left px-4 py-2 font-medium">
										{t("association_product_b")}
									</TableHead>
									<TableHead className="text-right px-4 py-2 font-medium">
										{t("association_lift")}
									</TableHead>
									<TableHead className="text-right px-4 py-2 font-medium">
										{t("association_co_purchases")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{associations.map((a) => (
									<TableRow
										key={`${a.product_a_id}-${a.product_b_id}`}
										className="border-b last:border-0"
									>
										<TableCell className="px-4 py-2 text-sm truncate max-w-[180px]">
											{a.product_a_title || a.product_a_id.slice(0, 8)}
										</TableCell>
										<TableCell className="text-center px-2 py-2 text-muted-foreground">
											↔
										</TableCell>
										<TableCell className="px-4 py-2 text-sm truncate max-w-[180px]">
											{a.product_b_title || a.product_b_id.slice(0, 8)}
										</TableCell>
										<TableCell className="text-right px-4 py-2 text-sm font-medium">
											{a.lift.toFixed(1)}×
										</TableCell>
										<TableCell className="text-right px-4 py-2 text-muted-foreground text-sm">
											{a.co_purchase_count}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</div>
			)}

			{/* Top Queries */}
			{topQueries && topQueries.length > 0 && (
				<div>
					<h3 className="text-sm font-medium mb-3">
						{t("top_queries_title")}
					</h3>
					<div className="rounded-lg border overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow className="border-b bg-muted/30">
									<TableHead className="text-left px-4 py-2 font-medium">
										{t("top_queries_query")}
									</TableHead>
									<TableHead className="text-right px-4 py-2 font-medium">
										{t("top_queries_count")}
									</TableHead>
									<TableHead className="text-right px-4 py-2 font-medium">
										{t("top_queries_conversion")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{topQueries.map((q) => (
									<TableRow
										key={q.query}
										className="border-b last:border-0"
									>
										<TableCell className="px-4 py-2">
											{q.query}
										</TableCell>
										<TableCell className="text-right px-4 py-2 text-muted-foreground">
											{q.count.toLocaleString()}
										</TableCell>
										<TableCell className="text-right px-4 py-2">
											<span
												className={
													q.conversion_rate > 0.05
														? "text-emerald-600"
														: "text-muted-foreground"
												}
											>
												{(q.conversion_rate * 100).toFixed(1)}%
											</span>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</div>
			)}

			{/* Trending Products */}
			<div>
				<h3 className="text-sm font-medium mb-3">
					{t("trending_products")}
				</h3>
				{trending && trending.length > 0 ? (
					<div className="rounded-lg border overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow className="border-b bg-muted/30">
									<TableHead className="w-8 text-center px-2 py-2 font-medium">
										#
									</TableHead>
									<TableHead className="text-left px-4 py-2 font-medium">
										{t("trending_product")}
									</TableHead>
									<TableHead className="text-right px-4 py-2 font-medium">
										{t("stat_impressions")}
									</TableHead>
									<TableHead className="text-right px-4 py-2 font-medium">
										{t("stat_clicks")}
									</TableHead>
									<TableHead className="text-right px-4 py-2 font-medium">
										{t("stat_purchases")}
									</TableHead>
									<TableHead className="text-right px-4 py-2 font-medium">
										{t("stat_conversion")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{trending.map((p, i) => {
									const convRate =
										p.clicks > 0
											? ((p.purchases / p.clicks) * 100).toFixed(1)
											: "—";
									return (
										<TableRow
											key={p.product_id}
											className="border-b last:border-0"
										>
											<TableCell className="text-center px-2 py-2 text-muted-foreground text-xs">
												{i + 1}
											</TableCell>
											<TableCell className="px-4 py-2 text-sm truncate max-w-[200px]">
												{p.title}
											</TableCell>
											<TableCell className="text-right px-4 py-2 text-muted-foreground text-sm">
												{p.impressions.toLocaleString()}
											</TableCell>
											<TableCell className="text-right px-4 py-2 text-muted-foreground text-sm">
												{p.clicks.toLocaleString()}
											</TableCell>
											<TableCell className="text-right px-4 py-2 text-sm font-medium">
												{p.purchases.toLocaleString()}
											</TableCell>
											<TableCell className="text-right px-4 py-2 text-sm">
												<span
													className={
														convRate !== "—" &&
														Number.parseFloat(convRate) > 5
															? "text-emerald-600"
															: "text-muted-foreground"
													}
												>
													{convRate === "—"
														? convRate
														: `${convRate}%`}
												</span>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</div>
				) : (
					<div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
						{t("no_data")}
					</div>
				)}
			</div>
		</div>
	);
}

function StatCard({
	label,
	value,
}: { label: string; value: number | string }) {
	return (
		<div className="rounded-lg border p-4">
			<p className="text-xs text-muted-foreground">{label}</p>
			<p className="text-xl font-semibold mt-0.5">
				{typeof value === "number" ? value.toLocaleString() : value}
			</p>
		</div>
	);
}

function FunnelChart({
	funnel,
	t,
}: { funnel: FunnelData; t: ReturnType<typeof useTranslation>["t"] }) {
	const stages = [
		{ key: "stat_searches", value: funnel.searches },
		{ key: "stat_views", value: funnel.product_views },
		{ key: "stat_add_to_cart", value: funnel.add_to_carts },
		{ key: "stat_checkouts", value: funnel.checkouts },
		{ key: "stat_purchases", value: funnel.purchases },
	];

	const max = Math.max(...stages.map((s) => s.value), 1);

	return (
		<div className="space-y-2">
			{stages.map((stage, i) => {
				const pct = (stage.value / max) * 100;
				const prevValue = i > 0 ? stages[i - 1].value : 0;
				const convRate =
					i > 0 && prevValue > 0
						? ((stage.value / prevValue) * 100).toFixed(1)
						: null;
				return (
					<div key={stage.key} className="flex items-center gap-3">
						<span className="text-xs text-muted-foreground w-24 text-right shrink-0">
							{t(stage.key)}
						</span>
						<div className="flex-1 relative h-7">
							<div
								className="absolute inset-y-0 left-0 rounded bg-accent/20 transition-all"
								style={{ width: `${pct}%` }}
							/>
							<div className="absolute inset-y-0 left-0 flex items-center px-2">
								<span className="text-xs font-medium">
									{stage.value.toLocaleString()}
								</span>
							</div>
						</div>
						<span className="text-[10px] text-muted-foreground w-16 shrink-0">
							{convRate
								? t("funnel_conversion", { rate: convRate })
								: ""}
						</span>
					</div>
				);
			})}
		</div>
	);
}
